import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import PublicNavbar from "@/components/feature/PublicNavbar";
import PublicFooter from "@/components/feature/PublicFooter";
import PageMeta from "@/components/feature/PageMeta";
import { api, formatDate, assetUrl, type NewsPost } from "@/lib/api";

export default function NoticiaPublicPage() {
  const { id } = useParams();
  const [post, setPost] = useState<NewsPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    // There is no api.getPublicNewsPost(id), we will fetch all and find, or just use the existing one if we had an endpoint.
    // Let's fetch all and filter since we don't have a specific GET public post route.
    api.getPublicNews()
      .then((news) => {
        const found = news.find((n) => n.id === id);
        if (found) setPost(found);
        else setError(true);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-bg-primary)] flex items-center justify-center">
        <PublicNavbar />
        <i className="ri-loader-4-line animate-spin text-4xl text-[var(--color-accent-oil)]"></i>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-[var(--color-bg-primary)] flex flex-col items-center justify-center p-4">
        <PublicNavbar />
        <div className="text-center mt-20">
          <i className="ri-error-warning-line text-6xl text-[var(--color-text-muted)] mb-4"></i>
          <h1 className="text-2xl font-bold text-white mb-2">Noticia no encontrada</h1>
          <p className="text-[var(--color-text-secondary)] mb-6">La noticia que buscas no existe o ha sido retirada.</p>
          <Link to="/" className="btn-primary">Volver al inicio</Link>
        </div>
      </div>
    );
  }

  const date = formatDate(post.published_at);

  return (
    <div className="min-h-screen bg-[var(--color-bg-primary)] pt-20">
      <PageMeta path={`/noticia/${post.id}`} title={`${post.title} | Noticias`} />
      <PublicNavbar />

      <article className="max-w-4xl mx-auto px-4 py-12 animate-[fadeIn_0.5s_ease-out]">
        <Link to="/#noticias" className="inline-flex items-center gap-2 text-sm text-[var(--color-text-secondary)] hover:text-white transition-colors mb-8">
          <i className="ri-arrow-left-line"></i> Volver a noticias
        </Link>

        <div className="flex items-center gap-3 text-sm mb-4">
          <span className="px-3 py-1 bg-[var(--color-accent-oil)]/10 text-[var(--color-accent-oil)] font-semibold rounded-full">
            {post.category}
          </span>
          <span className="text-[var(--color-text-muted)]">{date}</span>
        </div>

        <h1 className="text-3xl md:text-5xl font-bold text-white mb-8 leading-tight">
          {post.title}
        </h1>

        {post.image_url && (
          <div className="mb-10 rounded-2xl overflow-hidden border border-[var(--color-border)] shadow-2xl">
            <img 
              src={assetUrl(post.image_url)} 
              alt={post.title} 
              className="w-full h-auto max-h-[500px] object-cover" 
            />
          </div>
        )}

        <div className="prose prose-invert prose-lg max-w-none text-[var(--color-text-secondary)] leading-relaxed">
          {/* We use whitespace-pre-wrap to respect newlines in the content text area */}
          {post.content ? (
            <div className="whitespace-pre-wrap">{post.content}</div>
          ) : (
            <div className="whitespace-pre-wrap">{post.summary}</div>
          )}
        </div>
      </article>

      <PublicFooter />
    </div>
  );
}
